import { useGetNNQuery, useListCamerasQuery } from "@/core/cameraApi";
import { useCameraApiStatus, useCameraApiUrl } from "@/hooks";
import { useSelectedCameraStream } from "@/modules/camera-selection";
import {
  useCameraApiRetry,
  useCameraStreamRecovery,
} from "@/modules/camera-stream";
import { useRelayReconnect } from "@/modules/device";
import { EditProjectModal } from "@/modules/project/components/EditProjectModal";
import { useActiveProject } from "@/modules/project/hooks/useActiveProject";
import {
  LocalAccessDisabled,
  ModelRunning,
  NoCameraConfigured,
  NoCameraDetected,
  RelayUnavailable,
  SearchingForCamera,
} from "@/modules/ui";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  useVideoCapture,
  VideoCaptureProvider,
} from "../../context/VideoCaptureContext";
import { useCameraRestart } from "../../hooks/useCameraRestart";
import { Captured } from "../Captured";
import { CaptureSettings } from "../CaptureSettings";
import { LeaveRecordingDialog } from "../LeaveRecordingDialog";
import { LiveCapture } from "../LiveCapture";

export const CapturePage = () => {
  const cameraApiUrl = useCameraApiUrl();
  const [activeProject] = useActiveProject();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const isSwitchingProject =
    !activeProject || String(activeProject.id) !== urlProjectId;
  const [settingsOpen, setSettingsOpen] = useState(false);
  const {
    data: cameras,
    isLoading,
    isFetching,
  } = useListCamerasQuery(undefined, {
    skip: !cameraApiUrl || isSwitchingProject,
  });
  const retryCameraApi = useCameraApiRetry();
  const cameraApiStatus = useCameraApiStatus();
  const { reconnect, isReconnecting } = useRelayReconnect();

  const {
    cameraMxid: selectedCamera,
    streamName: selectedStream,
    setStream: setSelectedStream,
  } = useSelectedCameraStream();

  const [isStreaming, setIsStreaming] = useState(false);
  const [isSwitchingStream, setIsSwitchingStream] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const hasLoadedDashboardOnceRef = useRef(false);

  // Retry the WebRTC connection once the camera comes back — shared with the
  // Run page's inference preview, which hits the same dead end.
  useCameraStreamRecovery({
    isCameraAvailable: !!(cameras && cameras.length > 0),
    isLoading,
  });

  // isSuccess (not !!data) — RTK Query preserves the last successful body
  // across an error refetch, so checking `data` would keep the banner up
  // after Stop while the server now returns 404. isSuccess correctly
  // flips to false on a rejected refetch, matching useRunDeploy.
  const {
    data: isModelRunning,
    isLoading: isModelLoading,
    isError: isModelError,
  } = useGetNNQuery(
    { mxid: selectedCamera!, streamName: selectedStream! },
    { skip: !selectedCamera || !selectedStream || isSwitchingProject, refetchOnMountOrArgChange: true },
  );
  useEffect(() => {
    if (isModelRunning || isModelError)
      hasLoadedDashboardOnceRef.current = true;
  }, [isModelRunning, isModelError]);

  const isInitialModelLoad =
    isModelLoading && !hasLoadedDashboardOnceRef.current;

  const hasCameras = cameras && cameras.length > 0;

  const openSettings = activeProject ? () => setSettingsOpen(true) : undefined;

  const renderNoCamera = () => (
    <>
      <NoCameraDetected
        onRefresh={retryCameraApi}
        isRefreshing={isFetching}
        onOpenSettings={openSettings}
      />
      {settingsOpen && activeProject && (
        <EditProjectModal
          project={activeProject}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </>
  );

  const renderNoCameraConfigured = () => (
    <>
      <NoCameraConfigured onOpenSettings={openSettings} />
      {settingsOpen && activeProject && (
        <EditProjectModal
          project={activeProject}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </>
  );

  if (isSwitchingProject) {
    return <SearchingForCamera />;
  }

  // Why there is no URL matters: only one of these cases is something the
  // settings modal can fix, and showing "no camera" for the others sent people
  // to change a configuration that was already correct.
  if (cameraApiStatus.kind === "connecting") {
    return <SearchingForCamera />;
  }

  if (cameraApiStatus.kind === "relay-unavailable") {
    return (
      <RelayUnavailable
        message={cameraApiStatus.message}
        onRetry={reconnect}
        isRetrying={isReconnecting}
      />
    );
  }

  if (cameraApiStatus.kind === "local-access-revoked") {
    return (
      <>
        <LocalAccessDisabled
          cameraApiUrl={cameraApiStatus.cameraApiUrl}
          onOpenSettings={openSettings}
        />
        {settingsOpen && activeProject && (
          <EditProjectModal
            project={activeProject}
            onClose={() => setSettingsOpen(false)}
          />
        )}
      </>
    );
  }

  if (!cameraApiUrl) {
    return renderNoCamera();
  }

  if (isLoading) {
    return <SearchingForCamera url={cameraApiUrl} />;
  }

  if (!hasCameras) {
    return renderNoCamera();
  }

  // Cameras detected but the project has no camera set — deliberately no
  // fallback: the camera is picked explicitly in the project settings.
  if (!selectedCamera) {
    return renderNoCameraConfigured();
  }

  // Stream auto-pick pending, or the saved camera isn't reachable (stale
  // mxid: streams never load). Also covers the initial model-status load.
  if (!selectedStream || isInitialModelLoad) {
    return <SearchingForCamera url={cameraApiUrl} />;
  }

  if (isModelRunning && activeProject) {
    return <ModelRunning projectId={activeProject.id} />;
  }

  return (
    <VideoCaptureProvider
      mediaStream={mediaStream}
      projectId={activeProject?.id ?? null}
    >
      <CapturePageBody
        selectedCamera={selectedCamera}
        selectedStream={selectedStream}
        isStreaming={isStreaming}
        isSwitchingStream={isSwitchingStream}
        mediaStream={mediaStream}
        onSelectStream={setSelectedStream}
        onStreamSwitchingChange={setIsSwitchingStream}
        onStreamingChange={setIsStreaming}
        onMediaStreamChange={setMediaStream}
      />
      <LeaveRecordingDialog />
    </VideoCaptureProvider>
  );
};

interface CapturePageBodyProps {
  selectedCamera: string | null;
  selectedStream: string | null;
  isStreaming: boolean;
  isSwitchingStream: boolean;
  mediaStream: MediaStream | null;
  onSelectStream: (stream: string | null) => void;
  onStreamSwitchingChange: (isSwitching: boolean) => void;
  onStreamingChange: (isStreaming: boolean) => void;
  onMediaStreamChange: (stream: MediaStream | null) => void;
}

// Split so we can read recording state from context without violating the
// "can't useContext in the same component that renders its Provider" rule.
const CapturePageBody = ({
  selectedCamera,
  selectedStream,
  isStreaming,
  isSwitchingStream,
  mediaStream,
  onSelectStream,
  onStreamSwitchingChange,
  onStreamingChange,
  onMediaStreamChange,
}: CapturePageBodyProps) => {
  const { isRecording } = useVideoCapture();
  const [isIntervalCapturing, setIsIntervalCapturing] = useState(false);
  const restart = useCameraRestart({
    mxid: selectedCamera,
    streamName: selectedStream,
    onBeforeRestart: () => setIsIntervalCapturing(false),
  });

  // A rebooting device has no stream to capture from, and the peer connection
  // can take a while to notice it went away — so gate the capture controls on
  // the reboot too rather than on the WebRTC state alone.
  const canCapture = isStreaming && !isSwitchingStream && !restart.isRebooting;

  return (
    <div className="-m-6 grid min-h-0 flex-1 grid-cols-[minmax(250px,1fr)_minmax(500px,2fr)_minmax(250px,1fr)] grid-rows-[minmax(0,1fr)] bg-white">
      <CaptureSettings
        selectedCamera={selectedCamera}
        selectedStream={selectedStream}
        isStreaming={canCapture}
        isIntervalCapturing={isIntervalCapturing}
        onIntervalCapturingChange={setIsIntervalCapturing}
        onSelectStream={onSelectStream}
        onStreamSwitchingChange={onStreamSwitchingChange}
        mediaStream={mediaStream}
        restart={restart}
      />
      <LiveCapture
        selectedCamera={selectedCamera}
        selectedStream={selectedStream}
        isSwitchingStream={isSwitchingStream}
        onStreamingChange={onStreamingChange}
        onMediaStreamChange={onMediaStreamChange}
        isRecording={isRecording}
        isIntervalCapturing={isIntervalCapturing}
        onIntervalCapturingChange={setIsIntervalCapturing}
        isRebooting={restart.isRebooting}
        rebootSecondsLeft={restart.rebootSecondsLeft}
      />
      <Captured />
    </div>
  );
};
